import {FC, useEffect} from 'react';
import {useCan} from 'src/permissions/hooks';
import {Box, Typography} from '@mui/material';
import {useLocation} from 'react-router-dom';
import {notificationApi} from '../../../api';
import {store} from '../../../app/store';
import {preloaderChangeStatus} from '../../../stores/common/commonSlice';
import {isErrorObject} from '../../../utils/helper';
import {NotifyError} from '../../../utils/notification';
import {useBusinessId, useAppSelector} from '../../../app/hooks';
import {moreThenNinetyNine} from '../../../utils/consts';
import {notificationsNumberSx} from './NotificationMarker.sx';

type GetNotificationTriggerType = ReturnType<
    typeof notificationApi.useLazyGetNotificationsQuery
>[0];

const handleGetNotifications = (
    trigger: GetNotificationTriggerType,
    businessId: number
) => {
    try {
        store.dispatch(preloaderChangeStatus(true));
        trigger({
            params: {
                size: 10,
                page: 0,
            },
            data: {
                businessId,
                read: false,
            },
        });
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    } finally {
        store.dispatch(preloaderChangeStatus(false));
    }
};

export const NotificationMarker: FC = () => {
    const location = useLocation();
    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();
    const canReadNotifications = useCan('notifications.search');
    const [getNotifications, notificationsData] = notificationApi.useLazyGetNotificationsQuery();
    const totalNotifications = notificationsData?.data?.page?.totalElements ?? 0;
    const formattedNotifications = totalNotifications > 99 ? moreThenNinetyNine : totalNotifications;

    // Asked for by permission rather than by who is asking: the count is of one business's
    // notifications, and the platform administrator - who was skipped here - is working in one.
    useEffect(() => {
        if (canReadNotifications) {
            handleGetNotifications(getNotifications, businessId);
        }
    }, [location, businessId]);

    if (totalNotifications === 0) {
        return null;
    }

    return (
        <Box sx={notificationsNumberSx}>
            <Typography variant="caption">{formattedNotifications}</Typography>
        </Box>
    );
};
